import ContentBody from '@op/components/ContentBody';
import React, { useMemo, useState } from 'react';
import { Layout } from 'tea-component';
import { IRealtimeStreaming } from './type';
import useLanguage from '@op/hooks/useLanguage';
import { PAGE_NAME } from '@op/constants/page';
import SvgIcon from '@home/components/SvgIcon';
import { useLocation } from 'umi';
import CardCommon from '@op/components/CardCommon';
import Filter, { FilterValueType } from './component/Filter';
import { DBO_TM } from '@op/utils/format';
import StreamingTable from './component/StreamingTable';
import useSteamingTable from './hooks/useSteamingTable';
import './index.scss';
import { useInitBuriedPointInfo } from '@op/operationLog/buriedPoint';
import { RealtimeStreaming as RealtimeStreamingPoint } from '@op/operationLog/pages';
import logo from '@op/assets/imgs/imarketing_logo.png';
import BeforeRequest from '@op/components/BeforeRequest';
import GameStreamInfo from './component/GameStreamInfo';
import { GST_GAME_ID, HOK_WORLD_GAME_ID } from '@op/constants/specialGame';

const filterSaveKey = 'DBORealtimeStreamingFilter';
const { Content } = Layout;
const RealtimeStreaming = (props: IRealtimeStreaming) => {
  const { gameInfo, gameInfoLoading } = props;
  const { formatMessage } = useLanguage();
  useInitBuriedPointInfo({ buriedPoint: RealtimeStreamingPoint });
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const timezone = DBO_TM.timezone();
  const status = searchParams.get('status');
  const { isDateRangeGame, isGST } = useMemo(() => {
    const gameId = gameInfo?.edition_unified_id;
    const isDateRangeGame = [HOK_WORLD_GAME_ID, GST_GAME_ID].includes(gameId);
    const isGST = gameId === GST_GAME_ID;

    return {
      isDateRangeGame,
      isGST,
    };
  }, [gameInfo]);

  function getDefaultFilter() {
    const defaultFilter = {
      ...DBO_TM.filterToday('daily'),
      date_type: 'daily',
      kolType: 'all',
      status: status ?? 'now',
      search_text: '',
      ...(isDateRangeGame ? { is_today: true } : {}),
    };

    if (location?.state?.useStorageFilter) {
      const filter = sessionStorage.getItem(filterSaveKey);
      if (filter) {
        return JSON.parse(filter);
      }
    }

    sessionStorage.setItem(filterSaveKey, JSON.stringify(defaultFilter));
    return defaultFilter;
  }

  const [filter, setFilter] = useState<FilterValueType>(getDefaultFilter());

  function onChange(value: Partial<FilterValueType>) {
    setFilter((data: FilterValueType) => {
      const filter = { ...data, ...value };

      sessionStorage.setItem(filterSaveKey, JSON.stringify(filter));

      return filter;
    });
  }

  const { realFilter, isCCV } = useMemo(() => {
    const { kolType, status, date_type: dateType, ...otherInfo } = filter;

    if (gameInfoLoading) return { realFilter: null, isCCV: status === 'now' };

    return {
      realFilter: {
        ...otherInfo,
        ...gameInfo,
        should_include_timeline: true,
        only_live_streaming: status === 'now',
        kol_types: kolType === 'all' ? [] : [kolType],
        date_type: dateType || 'daily',
      },
      isCCV: status === 'now',
    };
  }, [gameInfoLoading, filter]);

  const { download, time, ...tableData } = useSteamingTable({
    filter: realFilter,
    firstNoDataCallBack: () => {
      if (!status && filter.status === 'now') {
        setFilter({ ...filter, status: 'day' });
      }
    },
  });

  return (
    <Content className="dbo-page dbo-realtime-streaming-page">
      <ContentBody
        contentHeard={
          <Content.Header
            className="dbo-header"
            title={formatMessage({ id: PAGE_NAME.REALTIME_STREAMING })}
            subtitle={
              <div className="dbo-header-subtitle-wrapper">
                <div className="dbo-header-subtitle">
                  <SvgIcon type="clock" />
                  {formatMessage(
                    {
                      id: 'Update Time: {time} (UTC+{timezone})',
                    },
                    { time, timezone },
                  )}
                </div>
              </div>
            }
            operation={
              <div
                style={{
                  display: 'inline-flex',
                  color: '#838697',
                  fontSize: 14,
                  alignItems: 'center',
                }}
              >
                <img
                  src={logo}
                  style={{ width: 16, height: 16, marginRight: 6 }}
                />
                {formatMessage(
                  'Powered by iMarketing, contact bourneli@global.tencent.com for further questions',
                )}
              </div>
            }
          ></Content.Header>
        }
      >
        <>{!isDateRangeGame && <GameStreamInfo gameInfo={gameInfo} />}</>
        <CardCommon loading={gameInfoLoading} loadingHeight={500}>
          <Filter
            download={download}
            filter={filter}
            onChange={onChange}
            isGST={isGST}
            enableDateRange={isDateRangeGame}
          ></Filter>

          <StreamingTable
            {...tableData}
            isCCV={isCCV}
            filter={realFilter}
            isGST={isGST}
          ></StreamingTable>
        </CardCommon>
      </ContentBody>
    </Content>
  );
};

export default function Index() {
  return (
    <BeforeRequest ids={[]}>
      {(props) => <RealtimeStreaming {...props}></RealtimeStreaming>}
    </BeforeRequest>
  );
}
